/*
 * Copyright (c) 2025 Bolmate B.V.
 */

import {ResponseData} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useContext, useState} from 'react';
import {FREE_NOTIFICATION_TYPES} from '../../../../models/notificationModel';
import Subscription from '../../../../models/subscriptionModel';
import {NotificationType, SubscriptionModuleName} from '../../../../util/enums';
import {NotificationTypeParams} from '../../../../util/notificationUtil';
import {getNotificationAccounts} from '../../../../util/requestSender';
import {SubscriptionPricing} from '../../../../util/types';
import {createErrorMessage} from '../../../../util/utils';
import {AppContext} from '../../../misc/appContext';
import CurrentSubscription from './currentSubscription';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onClickFailedPayment: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onClickPortal: MouseEventHandler;
    onReload: VoidFunction;
    loading: boolean;
    subscription: Subscription;
    orderCount: number;
    usersEstimate: number;
    extraAccounts: number;
    currentPricing: SubscriptionPricing;
    discountPrice: number | null;
}

export default function CurrentSubscriptionController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const {
        onClickFailedPayment,
        onClickStart,
        onClickPortal,
        subscription,
        orderCount,
        usersEstimate,
        discountPrice,
        currentPricing,
        extraAccounts,
        onReload
    } = props;
    const [loading, setLoading] = useState(false);

    function onToggleNotificationsModule(activate: boolean, evt: any) {
        evt.stopPropagation();
        if (!account || !user) return;
        if (activate) onActivate();
        else checkUserHasNotifications();
    }

    function onActivate() {
        setLoading(true);
        subscription.addModule(
            {accountID: account!.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
            (subscription: Subscription, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar(
                        t('settings.subscription.notificationsActivated', 'De Notificaties module is geactiveerd')
                    );
                    user!.subscription = subscription;
                    context.onChangeContext({user});
                    onReload();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'settings.subscription.notificationsActivateFailedMsg',
                                    'Het activeren van de Notificaties module is niet gelukt.'
                                )
                        ),
                        t('settings.subscription.activateFailedTitle', 'Activeren mislukt')
                    );
            }
        );
    }

    function checkUserHasNotifications() {
        setLoading(true);
        getNotificationAccounts({
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const accounts: Record<string, Array<NotificationType>> = data.accounts_with_notification;
                    const blockingAccounts: Record<string, Array<NotificationType>> = {};
                    for (const [account, notificationTypes] of Object.entries(accounts)) {
                        const blockingTypes = notificationTypes.filter(
                            (notificationType: NotificationType) => !FREE_NOTIFICATION_TYPES.includes(notificationType)
                        );
                        if (blockingTypes.length) blockingAccounts[account] = blockingTypes;
                    }
                    if (Object.keys(blockingAccounts).length)
                        return showDialog(
                            <p>
                                {t(
                                    'settings.subscription.activeNotificationsHeader',
                                    'De volgende notificaties staan nog aan:'
                                )}
                                <br />
                                <br />
                                {Object.entries(blockingAccounts).map(renderActiveNotifications)}
                                {t(
                                    'settings.subscription.removeNotificationsHint',
                                    'Verwijder deze. Vervolgens is de Notificaties module te deactiveren.'
                                )}
                            </p>,
                            t('settings.subscription.activeNotificationsTitle', 'Actieve notificaties')
                        );
                }
                onCancelNotificationsModule();
            }
        });
    }

    function renderActiveNotifications(params: [string, Array<NotificationType>]) {
        return (
            <>
                <b>{params[0]}</b>
                <ul>
                    {params[1].map((notificationType: NotificationType, index: number) => (
                        <li key={index}>{NotificationTypeParams[notificationType].title}</li>
                    ))}
                </ul>
                <br />
            </>
        );
    }

    function onCancelNotificationsModule() {
        setLoading(true);
        subscription.cancelModule(
            {accountID: account!.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
            (subscription: Subscription, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar(
                        t('settings.subscription.notificationsDeactivated', 'De Notificaties module is gedeactiveerd')
                    );
                    user!.subscription = subscription;
                    context.onChangeContext({user});
                    onReload();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'settings.subscription.notificationsDeactivateFailedMsg',
                                    'Het deactiveren van de Notificaties module is niet gelukt.'
                                )
                        ),
                        t('settings.subscription.deactivateFailedTitle', 'Deactiveren mislukt')
                    );
            }
        );
    }

    return (
        <CurrentSubscription
            onClickFailedPayment={onClickFailedPayment}
            onClickStart={onClickStart}
            onClickPortal={onClickPortal}
            onToggleNotificationsModule={onToggleNotificationsModule}
            subscription={subscription}
            loading={loading || props.loading}
            extraAccounts={extraAccounts}
            currentPricing={currentPricing}
            discountPrice={discountPrice}
            orderCount={orderCount}
            usersEstimate={usersEstimate}
        />
    );
}

CurrentSubscriptionController.displayName = 'CurrentSubscriptionController';
